import type {
  AuthApiPermission,
  AuthApiUser,
  AuthApiUserRole,
  AuthApiUserRoleAssignment,
} from "@/types/auth-api-types";
import {
  backendPermissionCode,
  isSpaPermissionKey,
  spaKeyFromBackendCode,
} from "@/lib/auth/backend-permission-map";
import { ROLE_PERMISSION_CODES } from "@/seeders/role-permissions.seed";
import {
  LANDING_PATH_PRIORITY,
  ROLE_ALIASES,
  ROUTE_RULES,
  type AppRole,
  type NavIconKey,
  type Permission,
  type RouteAccessRule,
} from "@/lib/auth/rbac-config";

export {
  APP_ROLES,
  PERMISSIONS,
  ROUTE_RULES,
  LANDING_PATH_PRIORITY,
  ROLE_ALIASES,
  type AppRole,
  type Permission,
  type NavIconKey,
  type RouteAccessRule,
} from "@/lib/auth/rbac-config";

/** Higher priority roles are preferred for display. */
const ROLE_PRIORITY: readonly AppRole[] = [
  "ADMIN",
  "REVIEWER",
  "VIEWER",
  "AUDITOR",
];

const ROUTE_RULES_BY_LONGEST_PATH = [...ROUTE_RULES].sort(
  (a, b) => b.path.length - a.path.length,
);

export type NavItem = {
  title: string;
  url: string;
  icon: NavIconKey;
  section?: "platform" | "settings";
  activePaths?: string[];
};

function normalizePath(pathname: string): string {
  const path = pathname.split("?")[0]?.split("#")[0] ?? pathname;
  if (path === "/") return "/";
  return path.endsWith("/") ? path : `${path}/`;
}

function pathMatchesPrefix(normalizedPath: string, routePath: string): boolean {
  if (routePath === "/") {
    return normalizedPath === "/";
  }
  const prefix = `${routePath}/`;
  return normalizedPath === prefix || normalizedPath.startsWith(prefix);
}

function findRouteRule(normalizedPath: string): RouteAccessRule | null {
  for (const rule of ROUTE_RULES_BY_LONGEST_PATH) {
    if (pathMatchesPrefix(normalizedPath, rule.path)) {
      return rule;
    }
  }
  return null;
}

function roleFromString(value: string | undefined): AppRole | null {
  if (!value) return null;
  const key = value.trim().toUpperCase().replace(/\s+/g, "_");
  return ROLE_ALIASES[key] ?? null;
}

function roleFromAuthRole(role: AuthApiUserRole): AppRole | null {
  const code =
    typeof role.code === "string"
      ? role.code
      : typeof role.name === "string"
        ? role.name
        : undefined;
  return roleFromString(code);
}

function spaKeyFromApiPermission(
  permission: AuthApiPermission,
): Permission | null {
  if (typeof permission.code === "string") {
    const direct = permission.code.trim();
    if (isSpaPermissionKey(direct)) {
      return direct;
    }
    const fromCode = spaKeyFromBackendCode(direct);
    if (fromCode) return fromCode;
  }

  if (permission.resource && permission.action) {
    return spaKeyFromBackendCode(
      backendPermissionCode(permission.resource, permission.action),
    );
  }

  return null;
}

function normalizePermissionEntry(entry: unknown): AuthApiPermission | null {
  if (!entry || typeof entry !== "object") return null;

  const obj = entry as Record<string, unknown>;

  if (obj.permission && typeof obj.permission === "object") {
    return normalizePermissionEntry(obj.permission);
  }

  const resource =
    typeof obj.resource === "string" ? obj.resource : undefined;
  const action = typeof obj.action === "string" ? obj.action : undefined;
  const code = typeof obj.code === "string" ? obj.code : undefined;

  if (resource || action || code) {
    return {
      id: typeof obj.id === "string" ? obj.id : undefined,
      name: typeof obj.name === "string" ? obj.name : undefined,
      resource,
      action,
      code,
    };
  }

  return null;
}

function appendPermissions(
  target: AuthApiPermission[],
  value: unknown,
): void {
  if (!Array.isArray(value)) return;
  for (const entry of value) {
    const normalized = normalizePermissionEntry(entry);
    if (normalized) {
      target.push(normalized);
    }
  }
}

function collectPermissionsFromRoleLike(
  roleLike: unknown,
  target: AuthApiPermission[],
): void {
  if (!roleLike || typeof roleLike !== "object") return;

  const role = roleLike as Record<string, unknown>;

  if (role.role && typeof role.role === "object") {
    collectPermissionsFromRoleLike(role.role, target);
    return;
  }

  appendPermissions(target, role.permissions);
  appendPermissions(target, role.permissionDetails);
}

/** Collect permission rows from `/api/me` (user-level and nested under all roles). */
export function collectApiPermissions(
  user: AuthApiUser,
): AuthApiPermission[] {
  const collected: AuthApiPermission[] = [];

  appendPermissions(collected, user.permissions);

  for (const role of user.roles ?? []) {
    collectPermissionsFromRoleLike(role, collected);
  }

  for (const entry of user.userRoles ?? []) {
    collectPermissionsFromRoleLike(entry, collected);
  }

  if (user.role && typeof user.role === "object") {
    collectPermissionsFromRoleLike(user.role, collected);
  }

  return collected;
}

function mapApiPermissionsToSpaKeys(
  apiPermissions: readonly AuthApiPermission[],
): ReadonlySet<Permission> {
  const spaKeys = new Set<Permission>();

  for (const permission of apiPermissions) {
    const spaKey = spaKeyFromApiPermission(permission);
    if (spaKey) {
      spaKeys.add(spaKey);
    }
  }

  return spaKeys;
}

function mapRoleTemplatePermissions(
  roles: readonly AppRole[],
): ReadonlySet<Permission> {
  const spaKeys = new Set<Permission>();

  for (const role of roles) {
    const codes = ROLE_PERMISSION_CODES[role] ?? [];
    for (const code of codes) {
      const spaKey = spaKeyFromBackendCode(code);
      if (spaKey) {
        spaKeys.add(spaKey);
      }
    }
  }

  return spaKeys;
}

/** Map combined `/api/me` permissions (all roles) to SPA keys. */
export function resolveUserPermissions(
  user: AuthApiUser | null | undefined,
): ReadonlySet<Permission> {
  if (!user) return new Set();

  const fromApi = mapApiPermissionsToSpaKeys(collectApiPermissions(user));
  const fromRoles = mapRoleTemplatePermissions(resolveUserRoles(user));

  return new Set<Permission>([...fromApi, ...fromRoles]);
}

function resolveRoleField(
  role: string | AuthApiUserRole | undefined,
): AppRole | null {
  if (!role) return null;
  if (typeof role === "string") {
    return roleFromString(role);
  }
  return roleFromAuthRole(role);
}

function addRole(roles: Set<AppRole>, value: string | undefined) {
  const resolved = roleFromString(value);
  if (resolved) roles.add(resolved);
}

function collectRolesFromAssignment(
  roles: Set<AppRole>,
  assignment: AuthApiUserRoleAssignment,
) {
  const nested = assignment.role;
  if (nested) {
    const resolved = roleFromAuthRole(nested);
    if (resolved) roles.add(resolved);
    return;
  }
  addRole(roles, assignment.roleId);
}

/** Resolve all application roles from `/api/me` user payload. */
export function resolveUserRoles(
  user: AuthApiUser | null | undefined,
): AppRole[] {
  if (!user) return [];

  const roles = new Set<AppRole>();

  const direct = resolveRoleField(user.role);
  if (direct) roles.add(direct);

  for (const entry of user.roles ?? []) {
    const resolved = roleFromAuthRole(entry);
    if (resolved) roles.add(resolved);
  }

  for (const entry of user.userRoles ?? []) {
    collectRolesFromAssignment(roles, entry);
  }

  if (roles.size > 0) {
    return ROLE_PRIORITY.filter((role) => roles.has(role));
  }

  const devRole = process.env.NEXT_PUBLIC_DEV_ROLE;
  const fallback = roleFromString(devRole);
  return fallback ? [fallback] : [];
}

/** Primary role for display — highest priority among assigned roles. */
export function resolveUserRole(
  user: AuthApiUser | null | undefined,
): AppRole | null {
  const roles = resolveUserRoles(user);
  return roles[0] ?? null;
}

export function hasPermission(
  permissions: ReadonlySet<Permission>,
  permission: Permission,
): boolean {
  return permissions.has(permission);
}

export function hasAnyPermission(
  permissions: ReadonlySet<Permission>,
  required: readonly Permission[],
): boolean {
  if (required.length === 0) return false;
  return required.some((permission) => permissions.has(permission));
}

export function hasAllPermissions(
  permissions: ReadonlySet<Permission>,
  required: readonly Permission[],
): boolean {
  if (required.length === 0) return false;
  return required.every((permission) => permissions.has(permission));
}

export function canAccessRoute(
  pathname: string,
  permissions: ReadonlySet<Permission>,
): boolean {
  const normalized = normalizePath(pathname);
  const rule = findRouteRule(normalized);

  if (!rule) {
    return normalized === "/";
  }

  if (!rule.permission) {
    return true;
  }

  return permissions.has(rule.permission);
}

export function getDefaultHomePath(
  permissions: ReadonlySet<Permission>,
): string {
  for (const path of LANDING_PATH_PRIORITY) {
    if (canAccessRoute(path, permissions)) {
      return path;
    }
  }

  const firstNav = getNavItemsForPermissions(permissions)[0];
  if (firstNav) {
    return firstNav.url.endsWith("/") ? firstNav.url : `${firstNav.url}/`;
  }

  return "/profile/";
}

export function getNavItemsForPermissions(
  permissions: ReadonlySet<Permission>,
): NavItem[] {
  return ROUTE_RULES.filter(
    (rule) =>
      rule.nav &&
      (!rule.permission || permissions.has(rule.permission)),
  )
    .sort((a, b) => (a.nav!.order ?? 0) - (b.nav!.order ?? 0))
    .map((rule) => ({
      title: rule.nav!.title,
      url: rule.path,
      icon: rule.nav!.icon,
      section: rule.nav!.section,
      activePaths: rule.nav!.activePaths,
    }));
}

export function navigateToRoleHome(
  user: AuthApiUser | null | undefined,
): void {
  if (typeof window === "undefined") return;
  const permissions = resolveUserPermissions(user);
  window.location.replace(
    `${window.location.origin}${getDefaultHomePath(permissions)}`,
  );
}
